import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Edit, ExternalLink, FileText, Link as LinkIcon, Loader2, Paperclip, Printer, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import FilePreviewModal from '../components/FilePreviewModal';
import CaseHistory from '../components/CaseHistory';
import { formatDate, formatDateTime } from '../utils/helpers';
import { exportCaseToExcel } from '../utils/exportUtils';

const getOfficeHeading = (name) => {
  if (!name) return 'Office';
  const trimmed = String(name).trim();
  if (!trimmed) return 'Office';
  if (/^office of\b/i.test(trimmed)) return trimmed;
  return `Office of ${trimmed}`;
};

const Field = ({ label, value }) => (
  <div className="space-y-1">
    <div className="text-xs font-medium text-gray-500">{label}</div>
    <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">{value || '-'}</div>
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex items-center gap-3">
      <div className="p-2 bg-indigo-50 rounded-lg">
        {Icon ? <Icon className="w-5 h-5 text-indigo-600" /> : null}
      </div>
      <div className="text-sm font-semibold text-gray-800">{title}</div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const DispatchView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, post } = useApi();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [dispatch, setDispatch] = useState(null);
  const [existingFiles, setExistingFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [office, setOffice] = useState(null);
  const [linkedCases, setLinkedCases] = useState([]);

  const previewRef = useRef(null);

  useEffect(() => {
    const fetchDispatch = async () => {
      setLoading(true);
      try {
        const response = await get(`/dispatch/${id}`);
        setDispatch(response.data);
        setExistingFiles(response.data.attachments || []);
      } catch (e) {
        toast.error('Failed to load dispatch details');
        navigate('/dispatch');
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDispatch();
  }, [get, id, navigate]);

  useEffect(() => {
    (async () => {
      if (!id) return;
      try {
        const res = await get(`/relations/dispatch/${id}`);
        const all = Array.isArray(res?.data) ? res.data : [];
        const outgoing = all.filter(r => !String(r?.relationType || '').includes('(Incoming)'));
        setLinkedCases(outgoing);
      } catch (e) {
        void e;
        setLinkedCases([]);
      }
    })();
  }, [get, id]);

  useEffect(() => {
    const fetchOffice = async () => {
      if (!user?.officeId) return;
      try {
        const response = await get(`/offices/${user.officeId}`);
        setOffice(response.data);
      } catch (e) {
        setOffice(null);
      }
    };
    fetchOffice();
  }, [get, user?.officeId]);

  const pdfFiles = useMemo(
    () => existingFiles.filter(f => f?.mimeType === 'application/pdf'),
    [existingFiles]
  );

  const handleDownload = async (file) => {
    try {
      const blob = await get(`/files/${file.fileName}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.originalName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDownloadBundle = async () => {
    try {
      if (pdfFiles.length === 0) return;
      const fileIds = pdfFiles.map(f => f.id);
      const bundleName = `Dispatch_${dispatch?.dispatchNumber || id}.pdf`;
      const response = await post('/pdf/bundle', { fileIds, bundleName }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', bundleName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success('PDF downloaded');
    } catch (e) {
      console.error('Bundle failed', e);
      toast.error('Failed to download PDF');
    }
  };

  const handleDownloadCasePdf = async () => {
    try {
      const fileName = `Dispatch_${dispatch?.dispatchNumber || id}.pdf`;
      const timestamp = Date.now();
      const response = await get(`/pdf/case/dispatch/${id}?t=${timestamp}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (e) {
      console.error('PDF export failed', e);
      toast.error('Failed to export PDF');
    }
  };

  const handleDownloadCaseExcel = () => {
    try {
      if (!dispatch) return;
      const fileName = `Dispatch_${dispatch?.dispatchNumber || id}`;
      exportCaseToExcel({
        ...dispatch,
        officeName: office?.name || user?.officeName || 'Office',
        dispatchNumber: dispatch?.dispatchNumber || ''
      }, fileName);
      toast.success('Excel downloaded successfully');
    } catch (e) {
      console.error('Excel export failed', e);
      toast.error('Failed to export Excel');
    }
  };

  const getLinkHref = (link) => {
    if (!link?.type || !link?.id) return null;
    if (link.type === 'dispatch') return `/dispatch/${link.id}/view`;
    if (link.type === 'receipt') return `/diary/${link.id}/view`;
    if (link.type === 'efile') return `/efile/${link.id}`;
    if (link.type === 'efile_noting') return `/efile/${link.efileId || link.id}`;
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!dispatch) return null;

  return (
    <>
      <style>{`@media print{.no-print{display:none !important} body{background:#fff}}`}</style>

      <div className="max-w-5xl mx-auto mb-12">
        <div className="no-print flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dispatch')}
              className="hover:bg-gray-100 rounded-full p-2 transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Dispatch Preview</h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs font-medium px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full">
                    {dispatch.dispatchNumber || `#${dispatch.id}`}
                  </span>
                  {dispatch.priority ? <Badge priority={dispatch.priority}>{dispatch.priority}</Badge> : null}
                </div>
              </div>
              <div className="text-sm text-gray-500 mt-1">
                Created: {formatDateTime(dispatch.createdAt)} · Updated: {formatDateTime(dispatch.updatedAt)}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => window.open(`/dispatch/${id}/print`, '_blank', 'noopener,noreferrer')}
              className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Slip
            </Button>
            <Button
              variant="secondary"
              onClick={handleDownloadCasePdf}
              className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button
              variant="secondary"
              onClick={handleDownloadCaseExcel}
              className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Excel
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate(`/dispatch/${id}`)}
              className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </div>
        </div>

        <div ref={previewRef} className="space-y-6">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-900 via-sky-900 to-indigo-900 text-white shadow-sm">
            <div className="relative p-6">
              <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-amber-400/20 to-transparent" />
              <div className="relative flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-white/40 bg-white/10 text-[10px] font-black tracking-[0.24em] text-white/90">
                  GOP
                </div>
                <div className="flex-1 text-center">
                  <div className="text-lg font-black uppercase tracking-[0.18em] text-white">WATER AND POWER DEVELOPMENT</div>
                  <div className="mt-1 text-base font-semibold tracking-wide text-sky-100">
                    {getOfficeHeading(office?.name || user?.officeName)}
                  </div>
                  <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-200">
                    Official Dispatch Record
                  </div>
                  <div className="mt-2 text-[11px] text-sky-100/90">
                    {office?.code || user?.officeCode ? `${office?.code || user?.officeCode}` : ''}
                    {(office?.code || user?.officeCode) && office?.address ? ' · ' : ''}
                    {office?.address || ''}
                  </div>
                </div>
                <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-white/30 bg-white/10 text-[10px] font-bold tracking-[0.18em] text-sky-100">
                  WAPDA
                </div>
              </div>
            </div>
          </div>

          <Section title="Dispatch Details" icon={FileText}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Subject" value={dispatch.subject} />
              <Field label="Dispatch No." value={dispatch.dispatchNumber} />
              <Field label="Date Issued" value={formatDate(dispatch.dateIssued)} />
              <Field label="Category" value={dispatch.category} />
              <Field label="Mode of Dispatch" value={dispatch.modeOfDispatch} />
              <Field label="Status" value={dispatch.status} />
              <Field label="Confidential" value={dispatch.isConfidential ? 'Yes' : 'No'} />
            </div>
          </Section>

          <Section title="Recipient Info" icon={ExternalLink}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Recipient" value={dispatch.recipient} />
              <Field label="Recipient Department" value={dispatch.recipientDept} />
              <Field label="Recipient Address" value={dispatch.recipientAddress} />
            </div>
          </Section>

          <Section title="Delivery & Notes" icon={FileText}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Delivered To" value={dispatch.deliveredTo} />
              <Field label="Sending Officer" value={dispatch.sendingOfficerName} />
              <Field label="Designation" value={dispatch.sendingOfficerDesignation} />
              <Field label="Office" value={dispatch.sendingOffice} />
            </div>
          </Section>

          <Section title="Compliance Details" icon={ShieldCheck}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="File Reference" value={dispatch.fileReference} />
              <Field label="Enclosures" value={dispatch.enclosures} />
              <Field label="Priority" value={dispatch.priority} />
              <Field label="Comments" value={dispatch.remarks} />
            </div>
          </Section>

          <Section title="Attachments" icon={Paperclip}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="text-sm text-gray-600">
                {existingFiles.length} files
              </div>
              <div className="no-print flex items-center gap-2">
                <Button
                  variant="secondary"
                  onClick={handleDownloadBundle}
                  disabled={pdfFiles.length === 0}
                  className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm disabled:opacity-50"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF Bundle
                </Button>
              </div>
            </div>

            {existingFiles.length === 0 ? (
              <div className="text-sm text-gray-500">No attachments uploaded.</div>
            ) : (
              <div className="space-y-2">
                {existingFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 bg-white">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate" title={file.originalName}>
                        {file.originalName}
                      </div>
                      <div className="text-xs text-gray-500">{file.mimeType}</div>
                    </div>
                    <div className="no-print flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setPreviewFile(file);
                          setIsPreviewOpen(true);
                        }}
                        className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm"
                      >
                        Preview
                      </Button>
                      <Button
                        onClick={() => handleDownload(file)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Linked Cases" icon={LinkIcon}>
            {linkedCases.length === 0 ? (
              <div className="text-sm text-gray-500">No linked cases.</div>
            ) : (
              <div className="space-y-2">
                {linkedCases.map((link, idx) => {
                  const href = getLinkHref(link);
                  return (
                    <div key={`${link.type}-${link.id}-${idx}`} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 bg-white">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {link.number || `${String(link.type).toUpperCase()} #${link.id}`}
                          {link.relationType ? <span className="text-gray-500 font-normal"> · {link.relationType}</span> : null}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {link.subject || '-'} · {link.date ? formatDate(link.date) : '-'} · {link.status || '-'}
                        </div>
                      </div>
                      <div className="no-print flex-shrink-0">
                        {href ? (
                          <Button
                            variant="secondary"
                            onClick={() => navigate(href)}
                            className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm"
                          >
                            View
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </div>

        <div className="mt-8">
          <CaseHistory caseType="dispatch" caseId={id} />
        </div>
      </div>

      <FilePreviewModal
        file={previewFile}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </>
  );
};

export default DispatchView;
